import React, { useContext } from 'react';
import moment from 'moment/moment';
import { SUBMISSION_STATUS } from '@crazygames/game-types/src/submission/submission-status.type';
import { Box } from '@mui/material';
import Notification from '../../../../../../../../common/Styleguide/Common/Notification';
import { SubmissionContext } from '../../../Context/SubmissionProvider';
import { getLatestBuild } from '../../../../../../../../common/build-utils';
import { GAME_GUIDELINES_URL } from '../../UpdateSubmission.types';
import { DOCS_URL } from '@crazygames/game-types/src/submission/qa-results/qa-result.type';
import { AccordionSection } from '../../../../../Admin/Game/GameTab/utils';

const SubmissionTabVersionsNotifications: React.FC = () => {
  const { submission, isNonEditable } = useContext(SubmissionContext);

  if (!submission) {
    return null;
  }

  // Helpers
  const descendingBuilds = [...submission.gameBuilds].sort((a, b) => moment(b.createdAt).diff(moment(a.createdAt)));
  const showFeedbackStatuses: SUBMISSION_STATUS[] = [
    'AWAITING_DEVELOPER_CHANGE',
    'BASIC_LAUNCH_MIXED_RESULT',
    'BASIC_LAUNCH_APPROVED',
    'BASIC_LAUNCH_AWAITING_DEVELOPER_CHANGE',
  ];

  // Cases
  const showGoToQAInfo = descendingBuilds[0]?.status === 'DRAFT';
  const approvedForFullLaunch = submission.status === 'BASIC_LAUNCH_APPROVED' && submission.isFullLaunch;
  const waitingForReview = submission.status === 'NEW_SUBMISSION' || submission.status === 'SYNC_REQUIRED';
  const feedback =
    !isNonEditable && (showFeedbackStatuses.includes(submission.status) || getLatestBuild(submission.gameBuilds)?.status === 'REJECTED');
  const rejected = submission.status === 'REJECTED';

  const renderWaitingForReview = () => {
    return (
      <Notification
        component="li"
        theme="info"
        title="Waiting for review"
        description={<>Your game has been sent for review. We will notify you once the team has reviewed your game.</>}
      />
    );
  };

  const renderRejected = () => {
    return (
      <Notification
        component="li"
        theme="danger"
        title="Your submission has been rejected"
        description={
          <>
            Unfortunately, your game has been rejected. Make sure to read{' '}
            <a target="_blank" rel="noopener noreferrer" href={GAME_GUIDELINES_URL}>
              our documentation
            </a>{' '}
            to find all the guidelines and requirements regarding game submissions. Due to the large number of submissions we receive every{' '}
            day, we are unable to give you personalised feedback. Thank you for your understanding.
          </>
        }
      />
    );
  };

  const renderFeedback = () => {
    const { feedback } = submission;
    if (!feedback || feedback?.length < 1) {
      return null;
    }
    const lastFeedback = feedback.at(-1);
    const lastFeedbackDate = moment.utc(lastFeedback?.postedOn).format('DD.MM.YYYY');
    const title = `Feedback from ${lastFeedbackDate}`;

    return (
      <Notification
        component="li"
        theme="warning"
        title={title}
        description={
          <>
            Here's the feedback from our team:
            <br />
            {lastFeedback?.comment && <div dangerouslySetInnerHTML={{ __html: lastFeedback.comment }} />}
            {submission.status === 'BASIC_LAUNCH_APPROVED'
              ? 'Please submit a new version taking into account the Full Implementation requirements and our feedback.'
              : 'Please submit a new version with those change.'}
          </>
        }
      />
    );
  };

  const renderApprovedForFullLaunch = () => {
    return (
      <Notification
        component="li"
        theme="success"
        title="Congratulations!"
        description={
          <>
            Your game has been approved for Full Launch on CrazyGames. Please update your game to meet the requirements for the{' '}
            <a href={`${DOCS_URL}/requirements/technical/`} target="_blank" rel="noreferrer">
              Full Implementation
            </a>
            .
          </>
        }
      />
    );
  };

  const renderGoToQA = () => {
    return (
      <Notification
        component="li"
        theme="warning"
        title="Latest changes require QA"
        description={
          <>
            You didn't pass QA with your latest changes. Our content team will review them only after you passed the QA tool and submitted
            your update.
          </>
        }
      />
    );
  };

  const alerts: (JSX.Element | null)[] = [];

  if (showGoToQAInfo) {
    alerts.push(renderGoToQA());
  }

  if (approvedForFullLaunch) {
    alerts.push(renderApprovedForFullLaunch());
  }

  if (waitingForReview) {
    alerts.push(renderWaitingForReview());
  }

  if (feedback) {
    alerts.push(renderFeedback());
  }

  if (rejected) {
    alerts.push(renderRejected());
  }

  if (!alerts.length) {
    return null;
  }

  return (
    <AccordionSection title="Notifications" defaultExpanded>
      <Box component="ul" sx={{ p: 0, m: 0, display: 'flex', flexDirection: 'column', rowGap: 2 }}>
        {alerts.map((alert) => alert)}
      </Box>
    </AccordionSection>
  );
};

export default SubmissionTabVersionsNotifications;
